/** mu-api's NPC database (src/npc.ts) — every call carries the MegaCity bearer; a refused bearer signs the console out */
import { auth, bearerRefused } from './auth.svelte'
import type { Classes, Experience, Inputs, MindView, Persona, Readout, Rec, Records, Traits } from './types'

const API = ((import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://mu-api.psyopsys.ai/api').replace(/\/$/, '')

export class ApiError extends Error { constructor(public status: number, public code: string, message: string) { super(message) } }

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API}${path}`, { ...init, headers: { ...(init.headers ?? {}), Authorization: `Bearer ${auth.token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) } })
  if (r.status === 401) { bearerRefused(); throw new ApiError(401, 'signed_out', 'Your sign-in expired — sign in again.') }
  const j = await r.json().catch(() => ({})) as { error?: string; message?: string }
  if (!r.ok) throw new ApiError(r.status, j.error ?? 'error', j.message ?? `The NPC database answered ${r.status}.`)
  return j as T
}

export const api = {
  classes: () => call<Classes>('/npc/classes'),
  records: () => call<Records>('/npc/records'),
  record: (tenant: number) => call<Rec>(`/npc/record?tenant=${tenant}`),
  readout: (tenant: number) => call<Readout>(`/npc/readout?tenant=${tenant}`),
  save: (tenant: number, body: { revision: number; class?: string; inputs: Inputs }) =>
    call<Rec>(`/npc/record?tenant=${tenant}`, { method: 'POST', body: JSON.stringify(body) }),
  mind: (tenant: number) => call<MindView>(`/npc/mind?tenant=${tenant}`),
  requestMind: (tenant: number, body: { persona?: Persona; traits?: Traits; note?: string }) =>
    call<{ id: number; status: string }>(`/npc/mind/request?tenant=${tenant}`, { method: 'POST', body: JSON.stringify(body) }),
  experience: (tenant: number) => call<Experience>(`/npc/experience?tenant=${tenant}`),
  decideMind: (tenant: number, body: { id: number; accept: boolean }) =>
    call<{ id: number; status: string }>(`/npc/mind/decide?tenant=${tenant}`, { method: 'POST', body: JSON.stringify(body) }),
}
